// components/SubscriptionSection.tsx
'use client';

import React, { useState } from 'react';

// Define the structure for a feature in a plan
interface Feature {
  text: string;
  included: boolean;
}

// Define the structure for a subscription plan
interface Plan {
  id: string;
  name: string;
  description: string;
  price: number;
  pricePeriod: string;
  features: Feature[];
  buttonText: string;
  // isHighlighted prop is no longer needed here, will be managed by active state
  buttonHref?: string; // Optional link for the button
}

// Data for the subscription plans
const plansData: Plan[] = [
  {
    id: 'standard',
    name: 'Basic',
    description: 'Small businesses and startups needing a quick, affordable web presence.',
    price: 250,
    pricePeriod: '/ week',
    features: [
        { text: 'Professional Website Development', included: true },
        { text: 'Foundational SEO (Essential Search Engine Optimization)', included: true },
        { text: 'Ongoing Website Maintenance & Support', included: true },
        { text: 'Strategic Social Media Marketing', included: false },
        { text: 'Advanced Analytics & Innovative Growth Solutions', included: false },
        { text: 'Custom Graphic Design for Your Brand', included: false },
        { text: 'Bulk Product Upload & Data Management Services', included: false },
        { text: 'Custom Mobile Application Development', included: false },
        { text: 'Bespoke Web Application Development (Tailored Tech Stack)', included: false },
        { text: 'Custom Admin Panels & Internal Business Tools', included: false }
    ],
    buttonText: 'Get Started Now',
    buttonHref: '#contact',
  },
  {
    id: 'pro',
    name: 'PRO',
    description: 'Businesses aiming for better search visibility and consistent site performance.',
    price: 400,
    pricePeriod: '/ week',
    features: [
        { text: 'Professional Website Development', included: true },
        { text: 'Full SEO', included: true },
        { text: 'Ongoing Website Maintenance & Support', included: true },
        { text: 'Strategic Social Media Marketing', included: true },
        { text: 'Advanced Analytics & Innovative Growth Solutions', included: true },
        { text: 'Custom Graphic Design for Your Brand', included: false },
        { text: 'Bulk Product Upload & Data Management Services', included: false },
        { text: 'Custom Mobile Application Development', included: false },
        { text: 'Bespoke Web Application Development (Tailored Tech Stack)', included: false },
        { text: 'Custom Admin Panels & Internal Business Tools', included: false }
    ],
    buttonText: 'Get Started Now',
    buttonHref: '#contact',
  },
  {
    id: 'ultra', // This is the 3rd plan (index 2)
    name: 'ULTRA',
    description: 'Companies looking to scale and build a strong digital brand.',
    price: 750,
    pricePeriod: '/ week',
    features: [
        { text: 'Professional Website Development', included: true },
        { text: 'Full SEO', included: true },
        { text: 'Ongoing Website Maintenance & Support', included: true },
        { text: 'Strategic Social Media Marketing', included: true },
        { text: 'Advanced Analytics & Innovative Growth Solutions', included: true },
        { text: 'Custom Graphic Design for Your Brand', included: true },
        { text: 'Bulk Product Upload & Data Management Services', included: true },
        { text: 'Custom Mobile Application Development', included: false },
        { text: 'Bespoke Web Application Development (Tailored Tech Stack)', included: false },
        { text: 'Custom Admin Panels & Internal Business Tools', included: false }
    ],
    buttonText: 'Get Started Now',
    // isHighlighted: true, // Removed, will be handled by activePlanId state
    buttonHref: '#contact',
  },
  {
    id: 'max',
    name: 'MAX',
    description: 'Brands seeking full digital dominance and a comprehensive solution.',
    price: 1000,
    pricePeriod: '/ week',
    features: [
        { text: 'Professional Website Development', included: true },
        { text: 'Full SEO', included: true },
        { text: 'Ongoing Website Maintenance & Support', included: true },
        { text: 'Strategic Social Media Marketing', included: true },
        { text: 'Advanced Analytics & Innovative Growth Solutions', included: true },
        { text: 'Custom Graphic Design for Your Brand', included: true },
        { text: 'Bulk Product Upload & Data Management Services', included: true },
        { text: 'Custom Mobile Application Development', included: true },
        { text: 'Bespoke Web Application Development (Tailored Tech Stack)', included: true },
        { text: 'Custom Admin Panels & Internal Business Tools', included: true }
    ],
    buttonText: 'Get Started Now',
    buttonHref: '#contact',
  },
];

const defaultHighlightedPlanId = plansData[2].id; // ID of the 3rd plan ("ULTRA")

// Sub-component for rendering each subscription card
const SubscriptionCard: React.FC<Plan & { isActive: boolean; onHover: () => void }> = ({
  name,
  description,
  price,
  pricePeriod,
  features,
  buttonText,
  isActive, // New prop to determine if this card is the active one
  onHover,    // New prop to call when this card is hovered
  buttonHref,
}) => {
  const cardBaseClasses = "flex flex-col p-6 md:p-8 rounded-xl transition-all duration-300 ease-in-out h-full cursor-pointer"; // Added cursor-pointer
  const highlightedCardClasses = "bg-blue-50 text-gray-700 scale-105";
  const standardCardClasses = "bg-white text-gray-700 hover:shadow-2xl";

  const buttonBaseClasses = "hover:bg-blue-600 w-full py-3 px-6 rounded-full  font-semibold transition-colors duration-150 ease-in-out focus:outline-none focus:ring-2 focus:ring-opacity-75 text-center";
  const highlightedButtonClasses = "bg-blue-600 text-white hover:bg-blue-800 focus:ring-gray-200";
  const standardButtonClasses = "bg-transparent text-blue-500 hover:bg-blue-700 focus:ring-blue-400 border border-blue-600";


  return (
    <div 
      className={`${cardBaseClasses} ${isActive ? highlightedCardClasses : standardCardClasses}`}
      onMouseEnter={onHover} // Call the onHover function passed from parent
    >
      <h3 className={`text-sm font-semibold uppercase tracking-wider mb-1 text-blue-500`}>
        {name}
      </h3>
      <p className={`text-xs mb-4 min-h-[3em] text-gray-500`}>
        {description}
      </p>
      <div className="mb-6">
        <span className={`text-4xl md:text-5xl font-bold text-gray-900`}>
          ${price}
        </span>
        <span className={`text-sm ml-1 text-gray-500`}>
          {pricePeriod}
        </span>
      </div>
      <a
        href={buttonHref || '#'}
        className={`${buttonBaseClasses} ${isActive ? highlightedButtonClasses : standardButtonClasses} mb-6`}
      >
        {buttonText}
      </a>
      <ul className="space-y-2 text-sm flex-grow">
        {features.map((feature, index) => (
          <li key={index} className="flex items-start">
            {feature.included ? (
              <svg
                className={`w-5 h-5 mr-2 flex-shrink-0 text-blue-500`}
                fill="currentColor"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                ></path>
              </svg>
            ) : (
              <svg
                className={`w-5 h-5 mr-2 flex-shrink-0 text-gray-400`}
                fill="currentColor"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                ></path>
              </svg>
            )}
            <span className='text-gray-600'>{feature.text}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

// Main component for the subscription section
export const SubscriptionSection: React.FC<{ link?: string }> = ( { link } ) => {
  // State to track the ID of the currently active (highlighted) plan
  // Initialize with the ID of the default highlighted plan ("ULTRA")
  const [activePlanId, setActivePlanId] = useState<string>(defaultHighlightedPlanId);
  const handleMouseLeaveSection = () => {
    setActivePlanId(defaultHighlightedPlanId); // Reset to default when mouse leaves the grid
  };

  return (
    <section className="py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 md:mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight">
            POWERFUL SUBSCRIPTIONS
          </h2>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-blue-600 leading-tight mb-3">
            FOR POWERFUL CREATORS
          </h2>
          <p className="text-lg text-gray-600">
            Choose a plan that&apos;s right for you
          </p>
        </div>

        <div 
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 items-stretch"
          onMouseLeave={handleMouseLeaveSection} // Add onMouseLeave to the grid container
        >
          {plansData.map((plan) => (
            <SubscriptionCard 
    // Ensure buttonHref is passed to each card
              key={plan.id} 
              {...plan} 
                            buttonHref={link !== undefined ? link : plan.buttonHref} 
              isActive={plan.id === activePlanId} // Determine if this card is active
              onHover={() => setActivePlanId(plan.id)} // Set this card as active on hover
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default SubscriptionSection;
